import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { CareerValuation } from "@common/ADAPT.Common.Model/organisation/career-valuation";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { ChartUtils } from "@common/ux/base-ui.service/chart-utils";
import { IDxChartCustomizePoint, IDxChartTooltipPointInfo, IDxPieChartCustomizeLegendText } from "@common/ux/dx.types";
import { OrgCommonCareerValuationUiService } from "@org-common/lib/career-valuation/org-common-career-valuation-ui.service";
import { CulturalLeadershipFrameworkAuthService } from "@org-common/lib/cultural-leadership/cultural-leadership-framework-auth.service";
import { CulturalLeadershipFrameworkUiService } from "app/features/culture/cultural-leadership/cultural-leadership-framework-ui.service";
import { ICulturalLeadershipPersonalAnalysisDataWithCareerValuation } from "app/features/culture/cultural-leadership/cultural-leadership-personal-analysis-data.interface";
import { CareerValuationAnalysisDataService } from "app/features/people/career-valuation/career-valuation-analysis-data.service";
import { ContentReadyEvent as AccordionContentReadyEvent } from "devextreme/ui/accordion";
import dxPieChart, { InitializedEvent as PieChartInitializedEvent, piePointObject } from "devextreme/viz/pie_chart";
import { InitializedEvent as PolarChartInitializedEvent } from "devextreme/viz/polar_chart";
import { switchMap, tap } from "rxjs/operators";
import { CareerValuationService, ICareerValuationStatistics } from "../career-valuation.service";
import { ICareerValuationAnalysisFilter } from "../career-valuation-analysis-filter";
import { CareerValuationAuthService } from "../career-valuation-auth.service";
import { ICareerValuationChartTooltipOptions } from "../career-valuation-chart-tooltip-options.interface";
import { CareerValuationFilterService } from "../career-valuation-filter/career-valuation-filter.service";
import { CareerValuationUiService } from "../career-valuation-ui.service";

interface ICareerValuationOrbitData {
    index: number;
    value: number;
    careerValuation: CareerValuation;
}

interface ICareerValuationStatisticAccordionEntry {
    text: string;
    people: Person[];
}

@Component({
    selector: "adapt-cvt-organisation-statistics",
    templateUrl: "./analyse-cvt-organisation-statistics.component.html",
    styleUrl: "./analyse-cvt-organisation-statistics.component.css",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class CareerValuationOrganisationStatisticsComponent extends BaseComponent implements OnInit, OnChanges {
    @Input() public date?: Date;
    @Output() public dateChange = new EventEmitter<Date>();

    public idealsChart?: dxPieChart;

    public isLoading = true;

    public canSeeData = false;

    public activePeopleCount?: number;
    public filterParameters?: ICareerValuationAnalysisFilter;
    public statisticsData?: ICareerValuationStatistics;
    public valuationOrbitData?: ICareerValuationOrbitData[];
    public statisticsAccordion?: ICareerValuationStatisticAccordionEntry[];

    public idealsPalette = OrgCommonCareerValuationUiService.CareerValuationPalette.simpleSet;

    public overdueCatchupsPalette = CulturalLeadershipFrameworkUiService.CulturalLeadershipFrameworkPalette.simpleSet;

    public constructor(
        elementRef: ElementRef,
        private careerValuationService: CareerValuationService,
        public careerValuationUiService: CareerValuationUiService,
        private careerValuationAnalysisDataService: CareerValuationAnalysisDataService,
        private careerValuationAuthService: CareerValuationAuthService,
        public filterService: CareerValuationFilterService,
        private clfAuthService: CulturalLeadershipFrameworkAuthService,
    ) {
        super(elementRef);

        this.filterService.filterListener.pipe(
            tap((value) => this.filterParameters = value),
            switchMap(() => this.updateDataAndChart()),
            this.takeUntilDestroyed(),
        ).subscribe();
    }

    public async ngOnInit() {
        this.canSeeData = this.careerValuationAuthService.currentPersonHasAtLeastQuantitativeAccess()
            || await this.clfAuthService.currentPersonIsCulturalLeader();
    }

    public async ngOnChanges(changes: SimpleChanges) {
        await this.updateDataAndChart();
        this.dateChange.next(changes.date.currentValue);
    }

    public get careerValuationsHaveBeenRecorded() {
        return this.statisticsData && this.statisticsData.averageCareerValuation > 0;
    }

    public onIdealsChartInitialized(e: PieChartInitializedEvent) {
        this.idealsChart = e.component;
        this.onInitialized(e);
    }

    public onDistributionChartInitialized(e: PolarChartInitializedEvent) {
        ChartUtils.updateChartDimension(e);
        this.sizeChange$.subscribe(() => ChartUtils.updateChartDimension(e));
        this.careerValuationUiService.setOrbitChartStrips(e.component!);
    }

    public onInitialized(e: PieChartInitializedEvent) {
        ChartUtils.updateChartDimension(e);
        this.sizeChange$.subscribe(() => ChartUtils.updateChartDimension(e));
    }

    public onAccordionReady(e: AccordionContentReadyEvent) {
        setTimeout(() => e.component.updateDimensions(), 50);
    }

    @Autobind
    public customiseDistributionChartPoint(info: IDxChartCustomizePoint) {
        return this.careerValuationUiService.customiseChartPoint(info);
    }

    @Autobind
    public customiseDistributionTooltip(info: IDxChartTooltipPointInfo) {
        const tooltipOptions: ICareerValuationChartTooltipOptions = {
            showName: true,
        };

        return {
            html: this.careerValuationUiService.getChartDataTooltip(info.point.tag, tooltipOptions),
        };
    }

    @Autobind
    private async updateDataAndChart() {
        const filterParameters: ICareerValuationAnalysisFilter = { ...this.filterParameters!, date: this.date };
        const data = await this.careerValuationService.promiseToGetCareerValuationAnalysisData(filterParameters, false, true);
        this.statisticsData = this.careerValuationService.generateCareerValuationStatistics(data);
        this.statisticsAccordion = this.getStatisticsAccordionEntries(this.statisticsData);
        this.activePeopleCount = await this.careerValuationAnalysisDataService.getActivePeopleCount(filterParameters);
        this.valuationOrbitData = data
            // need to filter out the excluded people again (included above to show them as excluded in the people count panel)
            // this narrows the type so we don't have to assert via !
            .filter((analysisData): analysisData is ICulturalLeadershipPersonalAnalysisDataWithCareerValuation =>
                !!analysisData.careerValuation && !analysisData.personIsExcluded)
            .map((analysisData, index) => {
                return {
                    index,
                    value: 100 - analysisData.careerValuation.extensions.actualTotal, // as we are storing higher values closer to the center we must take the actual value away from the maximum possible value
                    careerValuation: analysisData.careerValuation,
                };
            });
        this.isLoading = false;
    }

    private getStatisticsAccordionEntries(data: ICareerValuationStatistics): ICareerValuationStatisticAccordionEntry[] {
        const entries: ICareerValuationStatisticAccordionEntry[] = [];

        if (data.missingCareerValuations.length > 0) {
            entries.push({ text: `${data.missingCareerValuations.length} people without a career valuation`, people: data.missingCareerValuations });
        }

        if (data.excludedPeople.length > 0) {
            entries.push({ text: `${data.excludedPeople.length} people excluded from cultural statistics`, people: data.excludedPeople });
        }

        return entries;
    }

    @Autobind
    public customizeIdealsChartLegendText(info: IDxPieChartCustomizeLegendText) {
        if (this.idealsChart) {
            const series = this.idealsChart.instance().getSeriesByPos(0);
            const point = series.getPointByPos(info.pointIndex!) as piePointObject;

            return info.pointName + " (" + Math.floor(Number(point.percent) * 100) + "%)";
        }

        return "";
    }
}
